import { useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileUp, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const MONTH_NAMES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const MONTHS = MONTH_NAMES.map((_, i) => String(i + 1).padStart(2, "0"));
const YEARS = Array.from({ length: 7 }, (_, i) => String(new Date().getFullYear() + 1 - i));

const selectCls =
  "h-10 rounded-md border border-input bg-white px-3 text-sm transition-colors hover:border-accent/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40";

const norm = (s) =>
  (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

function match(filename, employees) {
  const base = norm(filename.replace(/\.[^.]+$/, ""));
  const digits = (filename.match(/\d{7,8}[a-zA-Z]?/g) || []).map((d) => d.toLowerCase());
  const byDni = employees.find((e) => e.dni && digits.some((d) => d.replace(/\W/g, "") === e.dni.toLowerCase().replace(/\W/g, "")));
  if (byDni) return { id: byDni.id, how: "DNI" };
  let best = null;
  for (const e of employees) {
    const parts = norm(e.name).split(" ").filter((p) => p.length > 2);
    const hits = parts.filter((p) => base.includes(p)).length;
    if (hits >= 2 && (!best || hits > best.hits)) best = { id: e.id, hits, how: "nombre" };
  }
  return best ? { id: best.id, how: best.how } : { id: "", how: "" };
}

export default function BulkPayslipsDialog({ open, employees, onClose, onDone }) {
  const fileRef = useRef();
  const singleRef = useRef();
  const now = new Date();
  const [tab, setTab] = useState("single");
  const [docType, setDocType] = useState("nomina");
  const [month, setMonth] = useState(MONTHS[now.getMonth()]);
  const [year, setYear] = useState(String(now.getFullYear()));
  const [notify, setNotify] = useState(true);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [split, setSplit] = useState(null);

  const active = useMemo(() => employees.filter((e) => e.active !== false), [employees]);

  const analyze = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/payslips/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSplit({ ...r.data, filename: file.name });
      const detected = r.data.groups.filter((g) => g.employee_id).length;
      if (r.data.ocr_pages) toast.success(`${r.data.pages_total} páginas · ${r.data.ocr_pages} escaneada(s) leídas con reconocimiento de imagen · ${detected} bloques reconocidos`);
      else toast.success(`${r.data.pages_total} páginas · ${detected} de ${r.data.groups.length} bloques reconocidos`);
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo analizar el PDF"); }
    finally { setBusy(false); }
  };

  const setGroupEmployee = (i, id) =>
    setSplit((s) => ({ ...s, groups: s.groups.map((g, k) => (k === i ? { ...g, employee_id: id, how: "manual" } : g)) }));

  const commit = async () => {
    const ready = split.groups.filter((g) => g.employee_id).length;
    if (!ready) return toast.error("Asigna al menos un bloque de páginas a un empleado");
    setBusy(true);
    try {
      const r = await api.post("/payslips/commit", {
        split_id: split.split_id, period: `${year}-${month}`, doc_type: docType, notify,
        groups: split.groups.map((g) => ({ pages: g.pages, employee_id: g.employee_id })),
      });
      toast.success(`${r.data.uploaded.length} nómina(s) repartidas${notify ? ` · ${r.data.notified} aviso(s) enviados` : ""}`);
      (r.data.skipped || []).forEach((s) => toast.warning(s));
      setSplit(null);
      onDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo repartir el PDF"); }
    finally { setBusy(false); }
  };

  const pick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setRows(files.map((f) => {
      const m = match(f.name, active);
      return { file: f, employee_id: m.id, how: m.how, status: "listo" };
    }));
  };

  const setEmployee = (i, id) => setRows((r) => r.map((x, k) => (k === i ? { ...x, employee_id: id, how: "manual" } : x)));

  const upload = async () => {
    const ready = rows.filter((r) => r.employee_id);
    if (!ready.length) return toast.error("Asigna al menos un archivo a un empleado");
    setBusy(true); setProgress(0);
    let ok = 0, fail = 0;
    const uploaded = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (!row.employee_id) continue;
      const fd = new FormData();
      fd.append("doc_type", docType);
      fd.append("period", `${year}-${month}`);
      fd.append("notify", notify ? "true" : "false");
      fd.append("file", row.file);
      try {
        const r = await api.post(`/employees/${row.employee_id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
        uploaded.push(r.data);
        setRows((rs) => rs.map((x, k) => (k === i ? { ...x, status: "subido" } : x)));
      } catch {
        fail += 1;
        setRows((rs) => rs.map((x, k) => (k === i ? { ...x, status: "error" } : x)));
      }
      setProgress(Math.round(((i + 1) / rows.length) * 100));
    }
    setBusy(false);
    const noMail = uploaded.filter((u) => u.notify_result && !u.notify_result.ok);
    toast[fail ? "warning" : "success"](
      `${ok} archivo(s) subidos${fail ? `, ${fail} con error` : ""}${notify ? ` · ${uploaded.length - noMail.length} aviso(s) enviados` : ""}`
    );
    if (noMail.length) toast.error(`Sin aviso: ${noMail.map((u) => u.notify_result.reason).join("; ")}`);
    onDone?.();
  };

  const close = () => { setRows([]); setProgress(0); setSplit(null); onClose(); };
  const assigned = rows.filter((r) => r.employee_id).length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-2xl w-[calc(100vw-1rem)] sm:w-full max-h-[92vh] overflow-hidden flex flex-col p-5 sm:p-6" data-testid="bulk-payslips-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Subida masiva de nóminas</DialogTitle>
          <DialogDescription>
            Elige el periodo, suelta todos los archivos del mes y revisa a qué trabajador va cada uno.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 overflow-y-auto py-2">
          <div className="inline-flex rounded-lg border p-1" data-testid="payslip-mode-toggle">
            {[["single", "Un PDF con todas"], ["multi", "Archivos individuales"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setTab(k)} data-testid={`payslip-mode-${k}`}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  tab === k ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium uppercase tracking-wider text-slate-500">Documento</Label>
              <select className={`${selectCls} w-full`} value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="bulk-doc-type">
                <option value="nomina">Nóminas</option>
                <option value="registro_jornada">Registro de jornada</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium uppercase tracking-wider text-slate-500">Mes</Label>
              <select className={`${selectCls} w-full`} value={month} onChange={(e) => setMonth(e.target.value)} data-testid="bulk-payslip-month">
                {MONTHS.map((m, i) => <option key={m} value={m}>{MONTH_NAMES[i]}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium uppercase tracking-wider text-slate-500">Año</Label>
              <select className={`${selectCls} w-full`} value={year} onChange={(e) => setYear(e.target.value)} data-testid="bulk-payslip-year">
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          <div>
            {tab === "single" ? (
              <>
                <input ref={singleRef} type="file" accept="application/pdf" className="hidden" onChange={analyze} data-testid="payslip-single-input" />
                <Button type="button" variant="outline" disabled={busy} onClick={() => singleRef.current?.click()} data-testid="payslip-single-choose">
                  {busy && !split ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileUp className="h-4 w-4 mr-2" />} Elegir el PDF con todas las nóminas
                </Button>
                <p className="mt-2 text-xs text-muted-foreground">
                  Se lee el texto de cada página y, si la nómina viene escaneada, se reconoce por imagen. Se agrupan las páginas de cada trabajador y revisas el reparto antes de confirmar.
                </p>
              </>
            ) : (
              <>
                <input ref={fileRef} type="file" multiple accept="application/pdf,image/*" className="hidden" onChange={pick} data-testid="bulk-payslip-input" />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="bulk-payslip-choose">
                  <FileUp className="h-4 w-4 mr-2" /> Elegir archivos
                </Button>
                <p className="mt-2 text-xs text-muted-foreground">
                  Se asigna cada archivo automáticamente por DNI o por el nombre del empleado en el nombre del archivo. Corrige a mano lo que no reconozca.
                </p>
              </>
            )}
          </div>

          {tab === "single" && split && (
            <ul className="divide-y rounded-lg border" data-testid="payslip-split-rows">
              {split.groups.map((g, i) => (
                <li key={i} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center" data-testid={`payslip-split-row-${i}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">
                      {g.pages.length === 1 ? `Página ${g.pages[0]}` : `Páginas ${g.pages[0]}–${g.pages[g.pages.length - 1]}`}
                    </p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                      {g.employee_id
                        ? <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Detectado por {g.how}</>
                        : <><AlertCircle className="h-3.5 w-3.5 text-amber-600" /> Sin reconocer · asígnalo a mano</>}
                    </p>
                  </div>
                  <select className={`${selectCls} sm:w-64`} value={g.employee_id} onChange={(e) => setGroupEmployee(i, e.target.value)}
                    data-testid={`payslip-split-select-${i}`}>
                    <option value="">Sin asignar (no se subirá)…</option>
                    {active.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </li>
              ))}
            </ul>
          )}

          {tab === "multi" && rows.length > 0 && (
            <ul className="divide-y rounded-lg border" data-testid="bulk-payslip-rows">
              {rows.map((r, i) => (
                <li key={`${r.file.name}-${i}`} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center" data-testid={`bulk-payslip-row-${i}`}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{r.file.name}</p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                      {r.status === "subido" && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                      {r.status === "error" && <AlertCircle className="h-3.5 w-3.5 text-red-600" />}
                      {r.status === "subido" ? "Subido" : r.status === "error" ? "Error al subir"
                        : r.employee_id ? `Detectado por ${r.how}` : "Sin asignar"}
                      {" · "}{(r.file.size / 1024).toFixed(0)} KB
                    </p>
                  </div>
                  <select className={`${selectCls} sm:w-64`} value={r.employee_id} onChange={(e) => setEmployee(i, e.target.value)}
                    data-testid={`bulk-payslip-select-${i}`}>
                    <option value="">Sin asignar…</option>
                    {active.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </li>
              ))}
            </ul>
          )}

          <label className="flex items-center gap-3 text-sm">
            <input type="checkbox" className="h-4 w-4 accent-primary" checked={notify}
              onChange={(e) => setNotify(e.target.checked)} data-testid="bulk-payslip-notify" />
            Avisar por correo a cada trabajador con enlace a su portal
          </label>

          {busy && (
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full bg-accent transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>

        <DialogFooter className="flex-row justify-end gap-2">
          <Button variant="outline" onClick={close} disabled={busy}>Cerrar</Button>
          {tab === "single" ? (
            <Button onClick={commit} disabled={busy || !split} data-testid="payslip-split-commit">
              {busy && split && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Repartir y guardar
            </Button>
          ) : (
            <Button onClick={upload} disabled={busy || !assigned} data-testid="bulk-payslip-upload">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Subir {assigned || ""} archivo{assigned === 1 ? "" : "s"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
